/**
 * What the manual block holds, by contract — ROADMAP 9.11.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * SO THAT A REDUCE IS PREDICTABLE BEFORE THE CLICK, NOT AFTER IT
 *
 * With netting on, a sell on a strike a trader already holds REDUCES the row
 * instead of adding one. That is the behaviour they asked for, and it has one
 * failure mode: nothing appears, so a trader who was not watching the drawer
 * assumes the click missed and clicks again.
 *
 * The fix is NOT a toast. `Terminal.tsx` removed success toasts deliberately —
 * "accepted is not filled", and a ladder worked at speed stacked one per
 * click. The answer is to put the holding on the CHAIN, where the decision is
 * made, so the outcome is known before the button is pressed.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * IT MUST MIRROR THE SERVER'S ELIGIBILITY, OR IT IS A PROMISE WE BREAK
 *
 * This counts exactly what `manualNetting.ts` would net against and nothing
 * else: the manual block, in ONE account, legs that are wholly and cleanly
 * held. A badge that also counted strategy legs would mark a strike where a
 * click opens a new position — which is worse than no badge, because the
 * trader would have read it as "this will reduce".
 *
 * The rules are therefore the same four, in the same order, and the comments
 * that explain WHY each one is there live in `manualNetting.ts` — this is the
 * browser's copy of a server decision and must not grow its own opinions.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { isGrouped, type OpenBookTrade } from './useOpenBook'

/** Signed UNITS and the lot they were traded in, per broker token. */
export interface HeldContract {
  /** Positive long, negative short. Summed across this account's manual legs. */
  readonly units: number
  /**
   * The lot size those units were traded in.
   *
   * Carried rather than re-read from the catalogue because `TradeLeg.lotSize`
   * is FROZEN at entry, and the chain's current one can differ after an
   * exchange revision. Showing lots computed from today's lot size would
   * state a size the position was never opened in.
   *
   * Zero when legs on one contract disagree about it — which is the same
   * condition that makes the server decline to net, so the badge then says
   * units rather than inventing a lot count.
   */
  readonly lotSize: number
}

/** Keyed `${connectionId}|${token}`. One account's view of one contract. */
export type ManualHeld = ReadonlyMap<string, HeldContract>

export const heldKey = (connectionId: string, token: number): string => `${connectionId}|${token}`

/**
 * Fold the open book into what each contract holds in the manual block.
 *
 * Exported and pure so the eligibility rules are testable without a browser,
 * a socket or a drawer.
 */
export function manualHeldFrom(trades: readonly OpenBookTrade[]): ManualHeld {
  const out = new Map<string, { units: number; lotSize: number | null }>()

  for (const t of trades) {
    // A strategy basket or a named combo is never netted — see the header.
    if (isGrouped(t)) continue
    // A single-leg combined-premium order has no label and no strategy, so
    // `isGrouped` calls it manual. It is not.
    if (t.premiumProtection !== undefined) continue
    if (t.status !== 'OPEN' && t.status !== 'ISSUE') continue

    for (const l of t.legs) {
      if (l.token === null) continue
      if (l.status !== 'FILLED') continue
      // The broker stopped reporting part of it; a review owns it now.
      if (l.orphanedQty !== null) continue
      if (l.netQty === 0) continue

      const key = heldKey(t.broker.connectionId, l.token)
      const prev = out.get(key)
      if (prev === undefined) {
        out.set(key, { units: l.netQty, lotSize: l.lotSize })
        continue
      }
      prev.units += l.netQty
      // Legs that disagree about the lot size have no shared lot count.
      if (prev.lotSize !== l.lotSize) prev.lotSize = null
    }
  }

  const held = new Map<string, HeldContract>()
  for (const [key, v] of out) {
    // A contract whose legs cancel out holds nothing and must not be marked.
    if (v.units === 0) continue
    held.set(key, { units: v.units, lotSize: v.lotSize ?? 0 })
  }
  return held
}

/**
 * How a holding reads on a chain row: a signed lot count, or signed units.
 *
 * LOTS when they divide cleanly, because lots are what the size box and the
 * netting plan are expressed in. Units with a `u` when they do not — a leg
 * left on a part-lot by a broker partial or an adjusted review genuinely
 * holds a quantity that is not a whole number of lots, and rounding it would
 * be the one thing worse than the longer string.
 *
 * Always signed, including the plus. The sign IS the information here, and a
 * bare "10" beside a strike reads as a price.
 */
export function heldLabel(h: HeldContract): string {
  const lots = h.lotSize > 0 && h.units % h.lotSize === 0 ? h.units / h.lotSize : null
  const n = lots ?? h.units
  return `${n > 0 ? '+' : ''}${n.toLocaleString('en-IN')}${lots === null ? 'u' : ''}`
}

/** The tooltip. Says what it is, and never promises what a click will do. */
export function heldTitle(h: HeldContract): string {
  const side = h.units > 0 ? 'long' : 'short'
  const lots = h.lotSize > 0 && h.units % h.lotSize === 0 ? h.units / h.lotSize : null
  const size = lots === null
    ? `${Math.abs(h.units).toLocaleString('en-IN')} units`
    : `${Math.abs(lots).toLocaleString('en-IN')} lot${Math.abs(lots) === 1 ? '' : 's'}`
  return `You are ${side} ${size} of this contract in this account, outside any strategy`
}

/**
 * EVERYTHING held on one contract, anywhere — ROADMAP 9.27.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * A DIFFERENT QUESTION FROM `manualHeldFrom`, WHICH IS WHY IT IS A SECOND
 * FUNCTION RATHER THAN A WIDER FIRST ONE
 *
 * `manualHeldFrom` answers "will a click here REDUCE?" and is a mirror of
 * the server's netting eligibility — one account, the manual block, cleanly
 * held legs. Its own header is explicit that counting strategy legs there
 * would mark a strike where a click opens a NEW position, which is worse
 * than no badge because the trader reads it as a promise.
 *
 * This answers "how much of this contract do I hold at all?" — across every
 * broker account, inside strategies, inside named groups and loose on the
 * chain. It promises nothing about what a click will do; it is exposure.
 *
 * Both are true at once and they frequently disagree: 20 lots of a strike
 * held inside an Iron Condor is a total of 20 and a nettable 0. The chain
 * shows the total as the NUMBER and uses the nettable figure to decide how
 * the chip is drawn, so the promise is still only made where it holds.
 *
 * Widening the first function instead would have been one number that
 * silently stopped meaning what `manualNetting.ts` means — the two were
 * written to be the same rules in the same order, and the comment saying so
 * would have become false.
 * ─────────────────────────────────────────────────────────────────────────────
 */
export type TotalHeld = ReadonlyMap<number, HeldContract>

export function totalHeldFrom(trades: readonly OpenBookTrade[]): TotalHeld {
  const out = new Map<number, { units: number; lotSize: number | null }>()

  for (const t of trades) {
    /*
     * NO `isGrouped` TEST and no account test — that is the whole
     * difference. What IS still excluded is anything that is not a live
     * holding: a trade that never opened, a leg still working, and a leg
     * the broker has stopped reporting, which a review owns rather than
     * the chain.
     */
    if (t.status !== 'OPEN' && t.status !== 'ISSUE') continue

    for (const l of t.legs) {
      if (l.token === null) continue
      if (l.status !== 'FILLED') continue
      if (l.orphanedQty !== null) continue
      if (l.netQty === 0) continue

      const prev = out.get(l.token)
      if (prev === undefined) {
        out.set(l.token, { units: l.netQty, lotSize: l.lotSize })
        continue
      }
      prev.units += l.netQty
      if (prev.lotSize !== l.lotSize) prev.lotSize = null
    }
  }

  const held = new Map<number, HeldContract>()
  for (const [token, v] of out) {
    // Long 10 and short 10 of one contract is flat, and a flat strike is
    // not an exposure to mark.
    if (v.units === 0) continue
    held.set(token, { units: v.units, lotSize: v.lotSize ?? 0 })
  }
  return held
}

/**
 * The tooltip for a total, which must never read as a netting promise.
 *
 * It says where the exposure is, because "you are long 20" on a strike that
 * a click would ADD to is the sentence a trader needs qualified.
 */
export function totalTitle(total: HeldContract, nettable: HeldContract | null): string {
  const side = total.units > 0 ? 'long' : 'short'
  const lots = total.lotSize > 0 && total.units % total.lotSize === 0
    ? total.units / total.lotSize
    : null
  const size = lots === null
    ? `${Math.abs(total.units).toLocaleString('en-IN')} units`
    : `${Math.abs(lots).toLocaleString('en-IN')} lot${Math.abs(lots) === 1 ? '' : 's'}`

  const head = `You are ${side} ${size} of this contract in total, across every account and strategy`
  if (nettable === null || nettable.units === 0) {
    return `${head}. None of it is loose on this account, so a click here opens a new position.`
  }
  if (nettable.units === total.units) return `${head}, and a click here reduces it.`
  return `${head}. ${heldLabel(nettable)} of it is loose on this account and a click here reduces that part.`
}
